import {
  Alert,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useState } from "react";
import {
  resetUkProgressForRestart,
  setAccountSelection,
  setApplicationStep,
  setUkResumeChoiceMade,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";

// "Welcome back" screen for UK users returning with saved onboarding
// progress. Offers continuing from the last checkpoint or starting fresh.
// Starting fresh clears only the re-doable steps — assessment attempts,
// lockout timers and the cooling-off countdown are preserved server-side.
export default function UKWelcomeBack() {
  const dispatch = useAppDispatch();
  const { ukOnboarding } = useOnboarding();
  const { checkpoints, selectedAccountType, progressExpired } = ukOnboarding;

  const [error, setError] = useState<string | null>(null);
  const [restartUkOnboarding, { isLoading: isRestarting }] =
    api.useRestartUkOnboardingMutation();

  const checkpointItems = [
    { label: "Account type selected", done: !!checkpoints?.accountTypeSelected },
    {
      label: "Risk warning & investor classification",
      done: !!checkpoints?.classificationDone,
    },
    { label: "Appropriateness assessment", done: !!checkpoints?.assessmentDone },
    { label: "Identity verification", done: !!checkpoints?.idVerificationDone },
    { label: "Application form", done: !!checkpoints?.applicationFormDone },
  ];

  const handleContinue = () => {
    // Restore the account-type choice so the routing effect resumes the
    // correct flow instead of asking again.
    if (selectedAccountType === "INDIVIDUAL") {
      dispatch(setAccountSelection("Individual"));
      dispatch(setUkResumeChoiceMade(true));
      // The Application routing effect now lands on the first incomplete step.
      dispatch(setApplicationStep("loading"));
      return;
    }
    // No saved account type (e.g. that step expired), or a saved ENTITY choice
    // from before entity onboarding was disabled for UK clients — re-ask step
    // one, which now only offers Individual.
    dispatch(setUkResumeChoiceMade(true));
    dispatch(setApplicationStep("accountType"));
  };

  const handleStartFresh = async () => {
    setError(null);
    try {
      await restartUkOnboarding().unwrap();
      dispatch(resetUkProgressForRestart());
      dispatch(setUkResumeChoiceMade(true));
      dispatch(setApplicationStep("accountType"));
    } catch (err: any) {
      setError(
        err?.data?.message || "We couldn't restart your application. Please try again.",
      );
    }
  };

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={600}>
          Welcome back
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          You have an application in progress. You can pick up where you left off, or start
          again from the beginning.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Your progress
          </Typography>
          <Stack spacing={1.5} mt={2}>
            {checkpointItems.map((item) => (
              <Stack key={item.label} direction="row" spacing={1.5} alignItems="center">
                {item.done ? (
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "20px" }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: "#9e9e9e", fontSize: "20px" }} />
                )}
                <Typography
                  variant="body1"
                  color={item.done ? "text.primary" : "text.secondary"}
                >
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Grid>

      {progressExpired && (
        <Grid item xs={12}>
          <Alert severity="warning">
            Some of your earlier application steps expired because more than 10 days passed
            since you started, so you&apos;ll need to complete them again.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <Alert severity="info">
          Starting fresh will not reset your assessment attempts or any waiting periods —
          completed assessments and cooling-off countdowns are kept.
        </Alert>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={handleStartFresh}
            disabled={isRestarting}
          >
            {isRestarting ? "Restarting..." : "Start fresh"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleContinue}
            disabled={isRestarting}
            sx={{ minWidth: 200 }}
          >
            Continue where I left off
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
